import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  assistantChat,
  cancelAssistantActionPlan,
  executeAssistantActionPlan,
  fetchAssistantActionPlan,
  fetchAssistantConversation,
  fetchPendingAssistantActionPlans,
  listAssistantConversations,
  revalidateAssistantActionPlan,
  type AssistantConversation,
  type AssistantMessage,
  type AssistantPlan,
  type AssistantProposal,
  type AssistantReadResult,
  type AssistantBugReportBadge
} from '../api/assistant.api';
import { ROUTE_PATHS } from '../../../app/router/route-paths';
import { useAuth } from '../../../shared/auth/use-auth';
import { userFacingActionError } from '../../../shared/lib/staff-user-facing-error';
import { useStaffToast } from '../../../shared/ui/StaffToast';
import { setDiagnosticsAssistantSnapshot } from '../../../shared/diagnostics/bug-context';

export type AssistantChatBubble = {
  role: 'user' | 'assistant';
  content: string;
  proposal?: AssistantProposal;
  actionError?: string;
  failedStep?: string;
  readResults?: AssistantReadResult[];
  bugReport?: AssistantBugReportBadge | null;
};

type AssistantChatContextValue = {
  conversations: AssistantConversation[];
  activeId: number | null;
  bubbles: AssistantChatBubble[];
  input: string;
  setInput: (value: string) => void;
  sending: boolean;
  loading: boolean;
  busyPlanId: number | null;
  error: string | null;
  /** «Следовать за агентом»: после успешного выполнения открыть расписание. */
  followAgent: boolean;
  setFollowAgent: (value: boolean) => void;
  newDialog: () => void;
  openConversation: (id: number) => void;
  send: () => void;
  sendText: (text: string) => Promise<void>;
  prefillInput: (text: string) => void;
  executePlan: (planId: number) => Promise<void>;
  cancelPlan: (planId: number) => Promise<void>;
  revalidatePlan: (planId: number, plan: AssistantPlan) => Promise<boolean>;
  reloadConversations: () => void;
  pendingPlans: AssistantProposal[];
  reloadPendingPlans: () => Promise<void>;
  failedMessage: string | null;
  retryLastMessage: () => Promise<void>;
};

const AssistantChatContext = createContext<AssistantChatContextValue | null>(null);

const FOLLOW_AGENT_KEY = 'domcrm-assistant-follow-agent';

function readFollowAgent(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(FOLLOW_AGENT_KEY) !== '0';
  } catch {
    return true;
  }
}

/**
 * Общее состояние чата помощника: плавающее окно и страница «Помощник» видят один диалог
 * и сохраняют его при переходах по страницам (провайдер смонтирован в AppShell).
 */
/**
 * `enabled=false` — у пользователя нет доступа к помощнику: список диалогов не запрашивается.
 */
export function AssistantProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const { apiClient } = useAuth();
  const toast = useStaffToast();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState<AssistantConversation[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [bubbles, setBubbles] = useState<AssistantChatBubble[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyPlanId, setBusyPlanId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const [followAgent, setFollowAgentState] = useState(readFollowAgent);
  const [pendingPlans, setPendingPlans] = useState<AssistantProposal[]>([]);

  useEffect(() => {
    setDiagnosticsAssistantSnapshot({
      conversation_id: activeId,
      plan_id: pendingPlans[0]?.plan_id ?? null
    });
  }, [activeId, pendingPlans]);

  const reloadPendingPlans = useCallback(async () => {
    if (!enabled) {
      setPendingPlans([]);
      return;
    }
    try {
      const items = await fetchPendingAssistantActionPlans(apiClient);
      setPendingPlans(items);
    } catch {
      setPendingPlans([]);
    }
  }, [apiClient, enabled]);

  useEffect(() => {
    void reloadPendingPlans();
    if (!enabled) return;
    const interval = setInterval(() => {
      void reloadPendingPlans();
    }, 20000);
    return () => clearInterval(interval);
  }, [enabled, reloadPendingPlans]);

  const reloadConversations = useCallback(async () => {
    if (!enabled) {
      setConversations([]);
      return;
    }
    try {
      setConversations(await listAssistantConversations(apiClient));
    } catch {
      setConversations([]);
    }
  }, [apiClient, enabled]);

  useEffect(() => {
    void reloadConversations();
  }, [reloadConversations]);

  const setFollowAgent = useCallback((value: boolean) => {
    setFollowAgentState(value);
    try {
      localStorage.setItem(FOLLOW_AGENT_KEY, value ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);

  const openConversation = useCallback(
    async (id: number) => {
      setActiveId(id);
      setLoading(true);
      setError(null);
      try {
        const items: AssistantMessage[] = await fetchAssistantConversation(apiClient, id);
        const mapped = await Promise.all(
          items.map(async (m) => {
            const bubble: AssistantChatBubble = {
              role: m.role === 'assistant' ? 'assistant' : 'user',
              content: m.content
            };
            if (m.role === 'assistant' && m.plan_id) {
              try {
                bubble.proposal = await fetchAssistantActionPlan(apiClient, m.plan_id);
              } catch {
                /* план мог истечь — карточку не показываем */
              }
            }
            return bubble;
          })
        );
        setBubbles(mapped);
      } catch (e) {
        setError(userFacingActionError(e));
      } finally {
        setLoading(false);
      }
    },
    [apiClient]
  );

  const newDialog = useCallback(() => {
    setActiveId(null);
    setBubbles([]);
    setError(null);
    setFailedMessage(null);
  }, []);

  const sendText = useCallback(
    async (rawText: string, isRetry = false) => {
      const text = rawText.trim();
      if (!text || sending) return;
      setInput('');
      setError(null);
      setFailedMessage(null);
      setBubbles((prev) => {
        const last = prev[prev.length - 1];
        if (isRetry && last && last.role === 'user' && last.content === text) {
          return prev;
        }
        return [...prev, { role: 'user', content: text }];
      });
      setSending(true);
      try {
        const reply = await assistantChat(apiClient, {
          message: text,
          conversation_id: activeId,
          current_route: typeof window !== 'undefined' ? window.location.pathname : undefined
        });
        setActiveId(reply.conversation_id);
        setFailedMessage(null);
        setBubbles((prev) => {
          const proposal = reply.proposal ?? undefined;
          const existing = proposal ? prev.some((b) => b.proposal?.plan_id === proposal.plan_id) : false;
          if (existing && proposal) {
            return [
              ...prev.map((b) =>
                b.proposal?.plan_id === proposal.plan_id
                  ? { ...b, proposal, actionError: reply.action_error, failedStep: reply.failed_step, bugReport: reply.bug_report }
                  : b
              ),
              { role: 'assistant', content: reply.reply, readResults: reply.read_results, bugReport: reply.bug_report }
            ];
          }
          return [
            ...prev,
            {
              role: 'assistant',
              content: reply.reply,
              proposal,
              actionError: reply.action_error,
              failedStep: reply.failed_step,
              readResults: reply.read_results,
              bugReport: reply.bug_report
            }
          ];
        });
        void reloadConversations();
      } catch (e) {
        const message = userFacingActionError(e);
        setError(message);
        setFailedMessage(text);
        toast({ variant: 'error', message });
      } finally {
        setSending(false);
      }
    },
    [activeId, apiClient, reloadConversations, sending, toast]
  );

  const retryLastMessage = useCallback(async () => {
    if (failedMessage) {
      await sendText(failedMessage, true);
    }
  }, [failedMessage, sendText]);

  const send = useCallback(() => {
    void sendText(input);
  }, [input, sendText]);

  const prefillInput = useCallback((text: string) => {
    setInput(text);
  }, []);

  const executePlan = useCallback(
    async (planId: number) => {
      if (busyPlanId !== null) return;
      setBusyPlanId(planId);
      setError(null);
      try {
        const res = await executeAssistantActionPlan(apiClient, planId);
        const proposal = res.proposal;
        setBubbles((prev) =>
          prev.map((b) =>
            b.proposal?.plan_id === planId
              ? { ...b, proposal: proposal ?? b.proposal, actionError: undefined, failedStep: res.failed_step }
              : b
          )
        );
        const content =
          res.status === 'executed'
            ? proposal?.summary
              ? `Готово: ${proposal.summary}`
              : 'Готово.'
            : 'План выполнен частично. Проверьте отчёт по шагам.';
        setBubbles((prev) => [...prev, { role: 'assistant', content }]);
        void reloadPendingPlans();
        if (res.status === 'executed' && followAgent) {
          navigate(ROUTE_PATHS.schedule);
        }
      } catch (e) {
        try {
          const fresh = await fetchAssistantActionPlan(apiClient, planId);
          setBubbles((prev) => prev.map((b) => (b.proposal?.plan_id === planId ? { ...b, proposal: fresh } : b)));
        } catch {
          /* ignore */
        }
        const message = userFacingActionError(e);
        setError(message);
        toast({ variant: 'error', message });
      } finally {
        setBusyPlanId(null);
      }
    },
    [apiClient, busyPlanId, followAgent, navigate, reloadPendingPlans, toast]
  );

  const cancelPlan = useCallback(
    async (planId: number) => {
      if (busyPlanId !== null) return;
      setBusyPlanId(planId);
      try {
        const res = await cancelAssistantActionPlan(apiClient, planId);
        if (res.proposal) {
          setBubbles((prev) => [
            ...prev.map((b) => (b.proposal?.plan_id === planId ? { ...b, proposal: res.proposal } : b)),
            { role: 'assistant', content: 'План отменён.' }
          ]);
        }
        void reloadPendingPlans();
      } catch (e) {
        const message = userFacingActionError(e);
        setError(message);
        toast({ variant: 'error', message });
      } finally {
        setBusyPlanId(null);
      }
    },
    [apiClient, busyPlanId, reloadPendingPlans, toast]
  );

  const revalidatePlan = useCallback(
    async (planId: number, plan: AssistantPlan): Promise<boolean> => {
      if (busyPlanId !== null) return false;
      setBusyPlanId(planId);
      try {
        const proposal = await revalidateAssistantActionPlan(apiClient, planId, plan);
        setBubbles((prev) =>
          prev.map((b) => (b.proposal?.plan_id === planId ? { ...b, proposal } : b))
        );
        return true;
      } catch (e) {
        const message = userFacingActionError(e);
        setError(message);
        toast({ variant: 'error', message });
        return false;
      } finally {
        setBusyPlanId(null);
      }
    },
    [apiClient, busyPlanId, toast]
  );

  const value = useMemo<AssistantChatContextValue>(
    () => ({
      conversations,
      activeId,
      bubbles,
      input,
      setInput,
      sending,
      loading,
      busyPlanId,
      error,
      followAgent,
      setFollowAgent,
      newDialog,
      openConversation,
      send,
      sendText,
      prefillInput,
      executePlan,
      cancelPlan,
      revalidatePlan,
      reloadConversations,
      pendingPlans,
      reloadPendingPlans,
      failedMessage,
      retryLastMessage
    }),
    [
      conversations,
      activeId,
      bubbles,
      input,
      sending,
      loading,
      busyPlanId,
      error,
      followAgent,
      setFollowAgent,
      newDialog,
      openConversation,
      send,
      sendText,
      prefillInput,
      executePlan,
      cancelPlan,
      revalidatePlan,
      reloadConversations,
      pendingPlans,
      reloadPendingPlans,
      failedMessage,
      retryLastMessage
    ]
  );

  return <AssistantChatContext.Provider value={value}>{children}</AssistantChatContext.Provider>;
}

export function useAssistantChat(): AssistantChatContextValue {
  const ctx = useContext(AssistantChatContext);
  if (!ctx) {
    throw new Error('useAssistantChat must be used within AssistantProvider');
  }
  return ctx;
}
